import { css } from '@emotion/react';
import getCourseAdminCategories from 'course_home/services/course-admin-categories-data';
import CredlyBadgeSettings from 'institutions/components/credly-badge-settings';
import HelpOptionsSettings from 'institutions/components/help-options-settings';
import URLS_TO_HIDE_ASK_NOVO_AI from 'institutions/components/ask-novoai/helpers';
import React, { useEffect } from 'react';
import { AngularContext } from 'react-app';
import { useDispatch, useSelector } from 'react-redux';
import { getHelpOptionsSettings, resetState } from 'redux/actions/help-options';
import t, { listen as reactTranslateListen } from 'react-translate';
import { Institution } from 'redux/schemas/models/institution';
import { getCurrentCourse, hasCurrentUserInProgressExportCommand } from 'redux/selectors/course';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { NvMenu } from 'shared/components/menu/nv-menu';
import { useCourseHomeUtils } from 'shared/hooks/use-course-home-utils';
import { useCourseContentExport } from 'shared/hooks/use-course-content-export';

const styles = css`
  .bs4-dropdown-menu {
    z-index: 1005;
  }
`;

export const CourseAdminMenu = () => {
  const { $scope, injectServices } = React.useContext(AngularContext);
  const dispatch = useDispatch();
  const [
    $state,
    $location,
    $uibModal,
    CurrentCourseManager,
    RailsRoutes,
    LecturePageManager,
    TimelinesManager,
    CurrentPermissionsManager,
    CurrentUserManager,
  ] = injectServices([
    '$state',
    '$location',
    '$uibModal',
    'CurrentCourseManager',
    'RailsRoutes',
    'LecturePageManager',
    'TimelinesManager',
    'CurrentPermissionsManager',
    'CurrentUserManager',
  ]);
  const institution: Institution = useSelector(getCurrentInstitution);
  const currentCourse = useSelector(getCurrentCourse);
  const isContentExporting = useSelector((state) => hasCurrentUserInProgressExportCommand(state, currentCourse?.catalogId));
  const userCourse = useSelector((state) => state.models.enrollments[currentCourse.userCourse]);
  const { isWhiteIcon, isEditOrReorderMode } = useCourseHomeUtils();

  const institutionHasSkilltags = institution?.hasSkillTags;
  // Logic to handle the credly badge configuration if the instituition has the credly integration
  const hasCredlyIntegration = institution.isCredlyEnabled ?? false;
  const handleCredlyBadgeRef = React.useRef<Function>();
  const handleHelpOptionsRef = React.useRef<Function>();
  const handleContentExportRef = React.useRef<Function>();
  const { startExport } = useCourseContentExport(currentCourse.catalogId);
  handleContentExportRef.current = () => startExport(currentCourse.name);
  const [transLoaded, setTransLoaded] = React.useState(false);

  React.useEffect(() => (
    reactTranslateListen(
      () => {
        setTransLoaded(true);
      },
    )
  ), []);

  React.useEffect(() => {
    if (transLoaded) {
      setTransLoaded(false);
    }
  }, [transLoaded]);

  const courseAdminCategories = React.useMemo(() => {
    let categories = getCourseAdminCategories(
      t,
      $state,
      $uibModal,
      RailsRoutes,
      CurrentPermissionsManager,
      currentCourse,
      userCourse,
      CurrentUserManager,
      hasCredlyIntegration,
      handleCredlyBadgeRef,
      institutionHasSkilltags,
      handleHelpOptionsRef,
      handleContentExportRef,
      isContentExporting,
    )
      .filter(category => category.show)
      .map(category => ({
        ...category,
        items: category.items.filter(item => item.show),
      }))
      .filter(category => category.items.length > 0);

    // For roles that only have one category, the icon and/or title is not shown
    if (categories.length === 1) {
      categories = categories.map(category => ({
        show: category.show,
        items: category.items,
      }));
    }

    return categories;
  }, [
    $uibModal,
    $state,
    RailsRoutes,
    CurrentPermissionsManager,
    hasCredlyIntegration,
    currentCourse,
    CurrentUserManager,
    isContentExporting,
    transLoaded,
  ]);


  const pageStates = [
    'team-facilitation',
    'user-management-learners',
    'user-management-course-admins',
    'announcements',
    'custom-downloads',
    'course-communications-edit',
  ];

  const selectedIfCurrentPageState = Boolean(
    pageStates.find(pageState => $scope.currentState === pageState),
  );

  useEffect(() => {
    const currentPath = $location.path();
    // Check if current route matches any URL that should trigger resetState
    const shouldResetState = URLS_TO_HIDE_ASK_NOVO_AI.some(url => currentPath.includes(url));
    if (shouldResetState) {
      dispatch(resetState());
      dispatch(getHelpOptionsSettings({ catalogId: currentCourse.catalogId }));
    }
  }, [$scope, $location, dispatch]);

  return (
    <div css={styles}>
      <NvMenu
        icon='admin-dashboard'
        isWhiteIcon={isWhiteIcon}
        categories={courseAdminCategories}
        isSelected={selectedIfCurrentPageState}
        labeledBy={t.INSTITUTIONS.DASHBOARD.ADMIN_DASHBOARD()}
        tabIndex={!isEditOrReorderMode ? 0 : -1}
        hasRhs={$scope.MainGridCtrl.hasRhs}
        tooltip={t.COURSE_ADMIN_MEGA_MENU.TOOLTIP()}
      />

      {hasCredlyIntegration
      && <CredlyBadgeSettings forwardShowModal={(func) => { handleCredlyBadgeRef.current = func; }} />}
      <HelpOptionsSettings forwardShowModal={(func) => { handleHelpOptionsRef.current = func; }} />
    </div>
  );
};

export default CourseAdminMenu;
